import React from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { DetrixSession } from '../types';
import type { TabId } from '../store/tabId';
import { useSessionUiStore } from '../store/useSessionUiStore';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface TabBarProps {
  session: DetrixSession;
}

export const TabBar: React.FC<TabBarProps> = ({ session }) => {
  const { activeTab, setActiveTab, setSelectedId } = useSessionUiStore();

  const networkCount = (session.network?.length ?? 0) + (session.browserResources?.length ?? 0);

  const tabs: { id: TabId; label: string; count: number }[] = [
    { id: 'steps', label: 'Steps', count: session.steps?.length ?? 0 },
    { id: 'actions', label: 'Actions', count: session.actions?.length ?? 0 },
    { id: 'network', label: 'Network', count: networkCount },
    { id: 'events', label: 'Events', count: session.events?.length ?? 0 },
  ];

  return (
    <nav className="relative z-50 flex h-[35px] shrink-0 items-center overflow-x-auto border-b border-[var(--border-subtle)] bg-[var(--bg-sidebar)] shadow-[var(--chrome-shadow-tab)] no-scrollbar">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => {
            setSelectedId(null);
            setActiveTab(tab.id);
          }}
          className={cn(
            'relative flex h-full user-select-none items-center gap-2 border-r border-[var(--border-subtle)] px-4 text-[12px] transition-colors',
            activeTab === tab.id
              ? 'bg-[var(--bg-editor)] text-[var(--text-primary)]'
              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
          )}
        >
          <span>{tab.label}</span>
          <span className="rounded-full bg-[var(--bg-elevated)] px-1.5 py-0.5 text-[10px] tabular-nums text-[var(--text-muted)]">
            {tab.count}
          </span>
          {activeTab === tab.id ? (
            <div className="absolute bottom-0 left-0 h-[1px] w-full bg-[var(--accent)]" />
          ) : null}
        </button>
      ))}
    </nav>
  );
};
